import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Mail, Phone, MapPin, MessageCircle, Send } from 'lucide-react';
import ScrollReveal from '@/components/animations/ScrollReveal';
import { useContent } from '@/hooks/useContent';
import { useToast } from '@/hooks/useToast';

gsap.registerPlugin(ScrollTrigger);

import { LAB_WHATSAPP_NUMBER, LAB_CALL_NUMBER } from '@/data/contact';

const WHATSAPP_NUMBER = LAB_WHATSAPP_NUMBER;

const Contact = () => {
  const { t } = useContent();
  const { showToast } = useToast();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  useEffect(() => {
    document.title = 'Contact Us - ElhamyLabs';
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const messageLines: string[] = [
      '📨 *طلب تواصل جديد*',
      '',
      formData.name ? `👤 *الاسم:* ${formData.name}` : '',
      formData.phone ? `📞 *رقم الهاتف:* ${formData.phone}` : '',
      formData.email ? `📧 *البريد الإلكتروني:* ${formData.email}` : '',
      '',
      formData.message ? `📝 *الرسالة:* ${formData.message}` : '',
    ].filter((line, index, arr) => {
      // keep blank lines only if followed by content to avoid duplicates at the end
      if (line.trim() !== '') return true;
      return index < arr.length - 1 && arr[index + 1].trim() !== '';
    });

    const whatsappMessage =
      messageLines.length > 0
        ? messageLines.join('\n')
        : `📨 *طلب تواصل جديد*\n📝 ${t('contact.sendMessage.title')}`;

    const encodedMessage = encodeURIComponent(whatsappMessage);
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;

    if (typeof window !== 'undefined') {
      window.open(whatsappUrl, '_blank');
    }

    showToast(t('contact.sendMessage.successMessage'), 'success');
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-brand-purple-dark via-brand-purple-base to-brand-purple-light dark:from-dark-primary dark:via-dark-secondary">
        <div className="container mx-auto px-4 text-center">
          <ScrollReveal>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white">
              {t('common.contact')}
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              {t('contact.subtitle')}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Contact Section */}
      <section ref={sectionRef} className="py-20 bg-light-primary dark:bg-dark-primary">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <ScrollReveal direction="right">
              <div>
                <h2 className="text-3xl font-bold mb-8 text-light-text dark:text-dark-text">{t('contact.contactInfo.title')}</h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-brand-purple-base/10 dark:bg-brand-orange-base/10 flex items-center justify-center flex-shrink-0">
                      <MessageCircle size={24} className="text-brand-purple-base dark:text-brand-orange-base" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2 text-light-text dark:text-dark-text">{t('contact.contactInfo.whatsappLabel')}</h3>
                      <div dir="ltr">
                        <a href={`https://wa.me/${LAB_WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer" className="block text-light-text-secondary dark:text-dark-text-secondary hover:text-brand-purple-base dark:hover:text-brand-orange-base transition-colors">
                          {t('contact.contactInfo.whatsapp')}
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-brand-purple-base/10 dark:bg-brand-orange-base/10 flex items-center justify-center flex-shrink-0">
                      <Phone size={24} className="text-brand-purple-base dark:text-brand-orange-base" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2 text-light-text dark:text-dark-text">{t('contact.contactInfo.phone')}</h3>
                      <div className="space-y-1" dir="ltr">
                        <a href={`tel:+${LAB_CALL_NUMBER}`} className="block text-light-text-secondary dark:text-dark-text-secondary hover:text-brand-purple-base dark:hover:text-brand-orange-base transition-colors">
                          {t('contact.contactInfo.phone1')}
                        </a>
                        <a href="tel:+20402212255" className="block text-light-text-secondary dark:text-dark-text-secondary hover:text-brand-purple-base dark:hover:text-brand-orange-base transition-colors">
                          {t('contact.contactInfo.phone2')}
                        </a>
                        <a href="tel:+20402213030" className="block text-light-text-secondary dark:text-dark-text-secondary hover:text-brand-purple-base dark:hover:text-brand-orange-base transition-colors">
                          {t('contact.contactInfo.phone3')}
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-brand-purple-base/10 dark:bg-brand-orange-base/10 flex items-center justify-center flex-shrink-0">
                      <Mail size={24} className="text-brand-purple-base dark:text-brand-orange-base" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1 text-light-text dark:text-dark-text">{t('contact.contactInfo.email')}</h3>
                      <a href="mailto:info@elhamylabs.com" className="text-light-text-secondary dark:text-dark-text-secondary hover:text-brand-purple-base dark:hover:text-brand-orange-base transition-colors">
                        info@elhamylabs.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-brand-purple-base/10 dark:bg-brand-orange-base/10 flex items-center justify-center flex-shrink-0">
                      <MapPin size={24} className="text-brand-purple-base dark:text-brand-orange-base" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1 text-light-text dark:text-dark-text">{t('contact.contactInfo.address')}</h3>
                      <p className="text-light-text-secondary dark:text-dark-text-secondary">
                        {t('contact.contactInfo.fullAddress')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            {/* Contact Form */}
            <ScrollReveal direction="left">
              <div className="p-8 bg-light-secondary dark:bg-dark-secondary rounded-xl">
                <h2 className="text-3xl font-bold mb-6 text-light-text dark:text-dark-text">{t('contact.sendMessage.title')}</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <input
                      type="text"
                      placeholder={t('contact.sendMessage.namePlaceholder')}
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full px-4 py-3 rounded-lg bg-light-primary dark:bg-dark-primary text-light-text dark:text-dark-text border border-light-text/10 dark:border-dark-text/10 focus:outline-none focus:ring-2 focus:ring-brand-purple-base dark:focus:ring-brand-orange-base"
                    />
                  </div>
                 
               
                  <div>
                    <textarea
                      placeholder={t('contact.sendMessage.messagePlaceholder')}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      rows={5}
                      className="w-full px-4 py-3 rounded-lg bg-light-primary dark:bg-dark-primary text-light-text dark:text-dark-text border border-light-text/10 dark:border-dark-text/10 focus:outline-none focus:ring-2 focus:ring-brand-purple-base dark:focus:ring-brand-orange-base resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full px-6 py-3 bg-brand-orange-base text-white rounded-lg hover:bg-brand-orange-light transition-colors font-semibold flex items-center justify-center gap-2"
                  >
                    <Send size={20} />
                    {t('contact.sendMessage.title')}
                  </button>
                </form>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;

